// Thumbnail/thumbnailUtils.js

// Hex 색상을 RGB로 변환
export const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
        `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` 
        : '0, 0, 0';
};

// Hex 색상을 RGBA로 변환
export const hexToRgba = (hex, alpha) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
        `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})` 
        : `rgba(0, 0, 0, ${alpha})`;
};

// 이미지를 지정된 크기에 맞게 리사이즈
export const resizeImageToFit = (img, targetWidth, targetHeight) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    const imgRatio = img.width / img.height;
    const targetRatio = targetWidth / targetHeight;
    
    let drawWidth, drawHeight, drawX, drawY;
    
    if (imgRatio > targetRatio) {
        drawHeight = targetHeight;
        drawWidth = drawHeight * imgRatio;
        drawX = (targetWidth - drawWidth) / 2;
        drawY = 0;
    } else {
        drawWidth = targetWidth;
        drawHeight = drawWidth / imgRatio;
        drawX = 0;
        drawY = (targetHeight - drawHeight) / 2;
    }
    
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    
    return canvas.toDataURL('image/jpeg', 0.8);
};

// 이미지 압축
export const compressImage = (imageFile, maxWidth, maxHeight, quality = 0.8) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > maxWidth || height > maxHeight) {
                    if (width / height > maxWidth / maxHeight) {
                        height = (maxWidth / width) * height;
                        width = maxWidth;
                    } else {
                        width = (maxHeight / height) * width;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        resolve(blob);
                    },
                    'image/jpeg',
                    quality
                );
            };
            img.onerror = reject;
            img.src = event.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
    });
};

// Base64 이미지를 Blob으로 변환
export const base64ToBlob = (base64, mimeType = 'image/jpeg') => {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
};

// 파일 다운로드 헬퍼
export const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
};

// 색상 밝기 계산 (텍스트 가독성을 위한)
export const getColorBrightness = (hex) => {
    const rgb = hexToRgb(hex).split(', ').map(Number);
    return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
};

// 색상이 밝은지 어두운지 판단
export const isLightColor = (hex) => {
    return getColorBrightness(hex) > 128;
};

// 대비되는 텍스트 색상 제안
export const getContrastTextColor = (backgroundColor) => {
    return isLightColor(backgroundColor) ? '#000000' : '#ffffff';
};

// 랜덤 색상 생성
export const generateRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

// 색상 팔레트 생성 (조화로운 색상들)
export const generateColorPalette = (baseColor, count = 5) => {
    const hsl = hexToHsl(baseColor);
    const palette = [baseColor];
    
    for (let i = 1; i < count; i++) {
        const newHue = (hsl.h + (360 / count) * i) % 360;
        const newColor = hslToHex(newHue, hsl.s, hsl.l);
        palette.push(newColor);
    }
    
    return palette;
};

// Hex를 HSL로 변환
export const hexToHsl = (hex) => {
    const rgb = hexToRgb(hex).split(', ').map(n => parseInt(n) / 255);
    const [r, g, b] = rgb;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
    };
};

// HSL을 Hex로 변환
export const hslToHex = (h, s, l) => {
    h /= 360;
    s /= 100;
    l /= 100;
    
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
    };

    let r, g, b;

    if (s === 0) {
        r = g = b = l;
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
    }

    const toHex = (c) => {
        const hex = Math.round(c * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// 텍스트 길이에 따른 폰트 크기 자동 조정
export const getOptimalFontSize = (text, maxWidth, baseFontSize = 40) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    let fontSize = baseFontSize;
    ctx.font = `${fontSize}px Arial`;
    
    while (ctx.measureText(text).width > maxWidth && fontSize > 12) {
        fontSize -= 2;
        ctx.font = `${fontSize}px Arial`;
    }
    
    return fontSize;
};

// 이미지 형식 감지
export const getImageFormat = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    const mimeType = file.type;
    
    const formats = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'webp': 'image/webp',
        'bmp': 'image/bmp'
    };
    
    return formats[extension] || mimeType || 'image/jpeg';
};